import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/sections/PageHero";
import { CTABand } from "@/components/sections/CTABand";
import { Lightbox } from "@/components/Lightbox";
import { IMAGES } from "@/lib/site";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BE = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BE}${u}`);

// Fallback tetris when no albums exist yet
const defaults = [
  { image_url: IMAGES.powder1, tag: "Powder Coating", title: "Production Booth Run" },
  { image_url: IMAGES.powder2, tag: "Architectural", title: "Long Extrusions" },
  { image_url: IMAGES.heroSparks, tag: "Media Blasting", title: "Structural Prep" },
  { image_url: IMAGES.rustedParts, tag: "Chemical Stripping", title: "Coating Removal" },
  { image_url: IMAGES.warehouse, tag: "Facility", title: "Production Floor" },
  { image_url: IMAGES.welding, tag: "Fabrication", title: "Pre-Coat Inspection" },
  { image_url: IMAGES.boltDetail, tag: "Automotive", title: "Detail Work" },
  { image_url: IMAGES.forklift, tag: "Logistics", title: "Large Part Handling" },
];

const SPANS = ["lg:col-span-8", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-6", "lg:col-span-6", "lg:col-span-12"];

export default function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/albums`).then(({ data }) => setAlbums(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Only show albums that have at least one photo
  const albumsWithPhotos = albums.filter(a => a.item_count > 0);

  return (
    <div data-testid="gallery-page">
      <PageHero
        eyebrow="Gallery"
        title={<>The floor, the <span className="text-[#FF5722]">parts</span>, the finish.</>}
        subtitle="A working shop, not a showroom. Real jobs, real substrates, real production runs."
        image={IMAGES.powder1}
      />

      <section className="section px-6 lg:px-10">
        {loading ? (
          <div className="text-zinc-500">Loading...</div>
        ) : albumsWithPhotos.length > 0 ? (
          <>
            <div className="mb-12">
              <div className="eyebrow mb-3">Albums</div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter">Browse by <span className="text-[#FF5722]">category.</span></h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4" data-testid="albums-tiles">
              {albumsWithPhotos.map((a, i) => (
                <Link
                  key={a.id}
                  to={`/gallery/${a.slug}`}
                  className={`group relative overflow-hidden border border-white/10 ${SPANS[i % SPANS.length]} min-h-[360px] block`}
                  data-testid={`album-tile-${a.slug}`}
                >
                  {a.cover_image_url && (
                    <img src={resolveUrl(a.cover_image_url)} alt={a.title} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-8 min-h-[360px]">
                    <div className="eyebrow mb-2">{a.item_count} photo{a.item_count === 1 ? "" : "s"}</div>
                    <div className="font-display text-3xl md:text-5xl tracking-tighter leading-none">{a.title}</div>
                    {a.description && <p className="text-zinc-300 mt-3 max-w-md">{a.description}</p>}
                    <div className="mt-4 flex items-center gap-2 text-[#FF5722] text-xs uppercase tracking-widest">
                      Explore Album <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
            {defaults.map((it, i) => (
              <button key={i} onClick={() => setLightboxIdx(i)} className={`group relative overflow-hidden border border-white/10 ${SPANS[i % SPANS.length]} min-h-[280px] text-left cursor-zoom-in`} data-testid={`gallery-item-${i}`}>
                <img src={resolveUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover absolute inset-0 opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="relative p-6 h-full flex flex-col justify-end min-h-[280px]">
                  <div className="eyebrow mb-2">{it.tag}</div>
                  <div className="font-display text-2xl md:text-3xl tracking-tight text-white">{it.title}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <Lightbox items={defaults} index={albums.filter(a => a.item_count > 0).length === 0 ? lightboxIdx : null} onClose={() => setLightboxIdx(null)} onIndex={setLightboxIdx} />

      <CTABand />
    </div>
  );
}
